/**
 * usePatientTabs Hook
 * 
 * Manages state for multiple patient tabs (multiple patient records open simultaneously)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { storage } from '@/utils/storage';
import { Patient } from '@/types/patient';

const LS_PATIENT_TABS = 'pp_patient_tabs_v1';
const LS_ACTIVE_TAB = 'pp_active_tab_v1';

export interface PatientTab {
  id: string;
  patientId: string | number;
  patient: Patient;
  colorIndex: number;
}

// 8 color options for tabs (matching prototype colors)
export const TAB_COLORS = [
  { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-white', border: 'border-blue-600', leftBorder: '#ef4444' }, // Red
  { bg: 'bg-green-500', hover: 'hover:bg-green-600', text: 'text-white', border: 'border-green-600', leftBorder: '#f59e0b' }, // Orange
  { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', text: 'text-white', border: 'border-purple-600', leftBorder: '#10b981' }, // Green
  { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', text: 'text-white', border: 'border-orange-600', leftBorder: '#3b82f6' }, // Blue
  { bg: 'bg-pink-500', hover: 'hover:bg-pink-600', text: 'text-white', border: 'border-pink-600', leftBorder: '#8b5cf6' }, // Purple
  { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', text: 'text-white', border: 'border-teal-600', leftBorder: '#ec4899' }, // Pink
  { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600', text: 'text-white', border: 'border-indigo-600', leftBorder: '#14b8a6' }, // Teal
  { bg: 'bg-red-500', hover: 'hover:bg-red-600', text: 'text-white', border: 'border-red-600', leftBorder: '#f97316' }, // Orange-red
];

export const usePatientTabs = () => {
  const [tabs, setTabs] = useState<PatientTab[]>(() => {
    const saved = storage.get<PatientTab[]>(LS_PATIENT_TABS);
    return saved || [];
  });

  const [activeTabId, setActiveTabId] = useState<string | null>(() => {
    const saved = storage.get<string | null>(LS_ACTIVE_TAB);
    return saved || null;
  });

  // Use refs to track if we're the source of changes (prevents infinite loops)
  const isInternalUpdateRef = useRef(false);
  const tabsRef = useRef(tabs);
  const activeTabIdRef = useRef(activeTabId);

  // Keep refs in sync
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  // Sync with localStorage changes from other components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (isInternalUpdateRef.current) return; // Ignore our own changes
      
      if (e.key === LS_PATIENT_TABS && e.newValue) {
        try {
          const newTabs = JSON.parse(e.newValue);
          // Only update if different
          const currentStr = JSON.stringify(tabsRef.current);
          const newStr = JSON.stringify(newTabs || []);
          if (currentStr !== newStr) {
            setTabs(newTabs || []);
          }
        } catch (error) {
          console.error('Failed to parse tabs from storage event:', error);
        }
      }
      if (e.key === LS_ACTIVE_TAB) {
        try {
          const newActiveTabId = e.newValue ? JSON.parse(e.newValue) : null;
          if (activeTabIdRef.current !== newActiveTabId) {
            setActiveTabId(newActiveTabId);
          }
        } catch (error) {
          console.error('Failed to parse activeTabId from storage event:', error);
        }
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (from same window)
    const handleCustomStorageChange = () => {
      if (isInternalUpdateRef.current) return; // Ignore our own changes
      
      const savedTabs = storage.get<PatientTab[]>(LS_PATIENT_TABS);
      const savedActiveTabId = storage.get<string | null>(LS_ACTIVE_TAB);
      
      // Only update if different
      if (savedTabs) {
        const currentStr = JSON.stringify(tabsRef.current);
        const newStr = JSON.stringify(savedTabs);
        if (currentStr !== newStr) {
          setTabs(savedTabs);
        }
      }
      
      if (activeTabIdRef.current !== savedActiveTabId) {
        setActiveTabId(savedActiveTabId);
      }
    };

    window.addEventListener('patientTabsChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('patientTabsChanged', handleCustomStorageChange);
    };
  }, []); // Empty deps - we use refs for current values

  // Persist tabs to localStorage and notify other components
  useEffect(() => {
    isInternalUpdateRef.current = true;
    storage.set(LS_PATIENT_TABS, tabs);
    // Dispatch event asynchronously to avoid immediate re-triggering
    setTimeout(() => {
      isInternalUpdateRef.current = false;
      window.dispatchEvent(new CustomEvent('patientTabsChanged'));
    }, 0);
  }, [tabs]);

  // Persist active tab to localStorage and notify other components
  useEffect(() => {
    isInternalUpdateRef.current = true;
    if (activeTabId) {
      storage.set(LS_ACTIVE_TAB, activeTabId);
    } else {
      storage.remove(LS_ACTIVE_TAB);
    }
    // Dispatch event asynchronously to avoid immediate re-triggering
    setTimeout(() => {
      isInternalUpdateRef.current = false;
      window.dispatchEvent(new CustomEvent('patientTabsChanged'));
    }, 0);
  }, [activeTabId]);

  const openPatient = useCallback((patient: Patient) => {
    setTabs((prevTabs) => {
      // Check if patient is already open
      const existingTab = prevTabs.find((tab) => tab.patientId === patient.id);
      if (existingTab) {
        // Switch to existing tab
        setActiveTabId(existingTab.id);
        return prevTabs;
      }

      // Create new tab
      const colorIndex = prevTabs.length % TAB_COLORS.length;
      const newTab: PatientTab = {
        id: `tab_${patient.id}_${Date.now()}`,
        patientId: patient.id,
        patient,
        colorIndex,
      };

      setActiveTabId(newTab.id);
      return [...prevTabs, newTab];
    });
  }, []);

  const closeTab = useCallback((tabId: string) => {
    setTabs((prevTabs) => {
      const newTabs = prevTabs.filter((tab) => tab.id !== tabId);
      
      // If closing active tab, switch to another tab
      if (activeTabId === tabId) {
        if (newTabs.length > 0) {
          // Switch to the tab that was before the closed one, or the first tab
          const closedIndex = prevTabs.findIndex((tab) => tab.id === tabId);
          const newActiveIndex = closedIndex > 0 ? closedIndex - 1 : 0;
          setActiveTabId(newTabs[newActiveIndex].id);
        } else {
          setActiveTabId(null);
        }
      }
      
      return newTabs;
    });
  }, [activeTabId]);

  const switchToTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const closeAllTabs = useCallback(() => {
    setTabs([]);
    setActiveTabId(null);
  }, []);

  const getActiveTab = useCallback((): PatientTab | null => {
    if (!activeTabId) return null;
    return tabs.find((tab) => tab.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  return {
    tabs,
    activeTabId,
    openPatient,
    closeTab,
    switchToTab,
    closeAllTabs,
    getActiveTab,
  };
};
